import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ScanFace, Search, Camera, ShoppingBag, Download, Instagram } from "lucide-react";
import { Header } from "@/components/Header";
import { EventCard } from "@/components/EventCard";
import { api, CATEGORIES } from "@/lib/api";

const MOSAIC = [
  { label: "Surf", src: "https://images.pexels.com/photos/15875334/pexels-photo-15875334.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { label: "Paraquedas", src: "https://images.unsplash.com/photo-1659221876406-31a3746f41b9?fm=jpg&q=80&w=700" },
  { label: "Bike", src: "https://images.unsplash.com/photo-1536244881128-90b1d3d2549f?fm=jpg&q=80&w=700" },
  { label: "Triathlon", src: "https://images.pexels.com/photos/5094957/pexels-photo-5094957.jpeg?auto=compress&cs=tinysrgb&w=700" },
  { label: "Corrida", src: "https://images.pexels.com/photos/18408962/pexels-photo-18408962.jpeg?auto=compress&cs=tinysrgb&w=700" },
];

const STEPS = [
  { icon: Search, title: "Escolha o evento", text: "Encontre a corrida, prova de bike, triathlon, surf ou festa em que você esteve." },
  { icon: Camera, title: "Envie uma selfie", text: "Tire na hora pela câmera ou envie uma foto do seu rosto." },
  { icon: ShoppingBag, title: "Escolha as fotos", text: "Mostramos só as fotos em que você aparece." },
  { icon: Download, title: "Baixe em alta", text: "Pague com segurança e baixe sem marca d'água." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("todos");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          (cat === "todos" || e.category === cat) &&
          (e.name + " " + e.location).toLowerCase().includes(q.toLowerCase())
      ),
    [events, q, cat]
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="relative grain overflow-hidden border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <span className="label-caps text-[#0055FF] flex items-center gap-2">
              <ScanFace className="w-4 h-4" /> Fotografia de esportes e Lifestyle
            </span>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
              Suas fotos.<br />Encontradas em<br /><span className="text-[#0055FF]">segundos.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-xl font-light">
              Envie uma Selfie ou Clique em ver Todas as Fotos
            </p>
            <a
              href="#eventos"
              data-testid="hero-find-events-btn"
              className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold transition-colors"
            >
              <Search className="w-4 h-4" /> Encontrar meu evento
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="grid grid-cols-2 grid-rows-3 gap-2 aspect-[4/5]" data-testid="hero-sports-mosaic">
              {MOSAIC.map((m, i) => (
                <div key={m.label} className={`group relative overflow-hidden rounded-lg bg-zinc-100 ${i === 0 ? "row-span-2" : ""}`}>
                  <img src={m.src} alt={m.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute bottom-2 left-2 label-caps bg-white/90 text-zinc-900 px-2 py-0.5 rounded text-[10px]">{m.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 border-b border-zinc-200">
        {STEPS.map((s, i) => (
          <div key={s.title} className={`p-5 ${i > 0 ? "lg:border-l" : ""} ${i % 2 ? "border-l lg:border-l" : ""} border-zinc-200`}>
            <span className="font-display text-sm text-zinc-400">0{i + 1}</span>
            <s.icon className="mt-3 w-6 h-6 text-[#0055FF]" />
            <h3 className="mt-3 font-semibold">{s.title}</h3>
            <p className="mt-1 text-sm text-zinc-600">{s.text}</p>
          </div>
        ))}
      </section>

      <section id="eventos" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <span className="label-caps text-zinc-500">Eventos</span>
            <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">Em qual evento você estava?</h2>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar evento ou local"
              data-testid="events-search-input"
              className="w-full h-12 pl-10 pr-4 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
            />
          </div>
        </div>
        <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
          {["todos", ...Object.keys(CATEGORIES)].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`category-filter-${c}`}
              className={`shrink-0 h-9 px-4 rounded-md text-sm font-medium border transition-colors ${cat === c ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 hover:border-zinc-900"}`}
            >
              {c === "todos" ? "Todos" : CATEGORIES[c]}
            </button>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {loading && [1, 2, 3].map((i) => <div key={i} className="h-[280px] rounded-lg bg-zinc-100 animate-pulse" />)}
          {!loading && filtered.map((e, i) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={i === 0 && filtered.length > 2 ? "md:col-span-2" : ""}
            >
              <EventCard event={e} large={i === 0 && filtered.length > 2} />
            </motion.div>
          ))}
        </div>
        {!loading && filtered.length === 0 && (
          <p className="mt-10 text-zinc-500" data-testid="events-empty">Nenhum evento encontrado.</p>
        )}
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <a href="/" data-testid="footer-goclick-btn" className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-zinc-900 hover:bg-[#0055FF] text-white font-display font-extrabold tracking-tight transition-colors">
            <ScanFace className="w-5 h-5" /> GO-CLICK
          </a>
          <a href="https://www.instagram.com/carlosflorofficial/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md text-sm bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white font-semibold transition-opacity">
            <Instagram className="w-4 h-4" /> @CARLOSFLOROFICIAL
          </a>
          <a href="/admin/login" data-testid="footer-admin-link" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold transition-colors">
            <Camera className="w-5 h-5" /> Área do fotógrafo
          </a>
        </div>
        <p className="pb-6 text-center text-xs text-zinc-400">© GO-CLICK · Esportes e Life Style</p>
      </footer>
    </div>
  );
}
